import { useState, useEffect, useContext } from 'react';
import { User } from 'firebase/auth';
import { authService, AuthUser } from '../services/authService';
import { AuthService as LocalAuthService } from '../services/auth-service';
import { SignupCredentials, LoginCredentials } from '../types/auth';

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('👂 Setting up unified Auth state listener');
    
    const unsubscribe = authService.onAuthStateChanged((firebaseUser: User | null) => {
      console.log('🔄 Auth state changed - Firebase notification received:', {
        user: firebaseUser ? firebaseUser.email : 'No user',
        uid: firebaseUser?.uid || 'N/A',
        timestamp: new Date().toISOString(),
        emulatorSource: 'http://localhost:9099'
      });
      
      if (firebaseUser) {
        // Try to get additional data from local storage
        const localUser = LocalAuthService.getCurrentUser();
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          firstName: localUser?.name?.first,
          lastName: localUser?.name?.last,
          phone: localUser?.phone,
          tenantId: localUser?.tenantId
        });
        setError(null); // Clear any previous errors
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      console.log('🛑 Cleaning up unified Auth state listener');
      unsubscribe();
    };
  }, []);

  const signUp = async (credentials: SignupCredentials): Promise<void> => {
    try {
      console.log('🎯 useAuth: Initiating unified sign up request');
      setError(null);
      setLoading(true);
      
      const authUser = await authService.signUp(credentials);
      console.log('🎯 useAuth: Sign up completed successfully', authUser);
      
      // The auth state listener will automatically update the user state
    } catch (error: any) {
      console.error('🎯 useAuth: Sign up failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (credentials: LoginCredentials): Promise<void> => {
    try {
      console.log('🎯 useAuth: Initiating unified sign in request');
      setError(null);
      setLoading(true);
      
      const authUser = await authService.signIn(credentials);
      console.log('🎯 useAuth: Sign in completed successfully', authUser);
      
      // The auth state listener will automatically update the user state
    } catch (error: any) {
      console.error('🎯 useAuth: Sign in failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      console.log('🎯 useAuth: Initiating unified sign out request');
      setError(null);
      await authService.signOut();
      console.log('🎯 useAuth: Sign out completed successfully');
      
      // The auth state listener will automatically update the user state
    } catch (error: any) {
      console.error('🎯 useAuth: Sign out failed:', error);
      setError(error.message);
      throw error;
    }
  };

  const login = signIn; // Alias for compatibility
  const isAuthenticated = !!user;
  const isLoading = loading;

  return {
    user,
    loading,
    error,
    signUp,
    signIn,
    login,
    signOut,
    isAuthenticated,
    isLoading
  };
};
